import React from 'react';
import { Link } from 'react-router-dom';
import { Cpu, Shield, Activity, Wifi, Smartphone, Zap, Terminal } from 'lucide-react';
import SEO from '../components/SEO';

const Technology: React.FC = () => {
  return (
    <div className="pt-24 pb-16">
      <SEO 
        title="Teknologi Kalibrasi GPS - Hardware Abstraction Layer"
        description="Pelajari bagaimana XCape mengoptimalkan akurasi GPS menggunakan Signal Processing dan Driver Level Abstraction."
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-xs font-mono uppercase tracking-wider border border-blue-200">
            <Terminal className="h-3 w-3" />
            Signal Engineering
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Under The Hood: <br />
            <span className="text-brand-primary">Hardware Abstraction</span>
          </h1>
          <p className="text-slate-500 max-w-3xl mx-auto text-lg">
            Jelajahi bagaimana XCape berinteraksi dengan <i>Location Driver</i> untuk membersihkan noise dan memastikan koordinat yang dikirim ke sistem operasi adalah data presisi yang Anda tentukan.
          </p>
        </div>

        {/* Core Architecture Concept */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Driver-Level Signal Injection</h2>
            <div className="prose prose-slate text-slate-600">
              <p className="mb-4">
                Masalah utama GPS standar adalah "Drifting" (pergeseran) akibat pantulan sinyal gedung atau cuaca buruk. Aplikasi absensi sering gagal memvalidasi lokasi karena ketidakstabilan ini.
              </p>
              <p className="mb-4">
                <strong>Pendekatan XCape Berbeda:</strong>
              </p>
              <p>
                XCape bekerja pada lapisan <strong>Hardware Abstraction Layer (HAL)</strong>. Kami membuat "Jembatan Sinyal" virtual. Saat Anda memilih lokasi di peta, XCape memberitahu driver GPS: <em>"Abaikan sinyal satelit yang lemah/noise, dan kunci koordinat pada titik referensi ini."</em> Hasilnya adalah lokasi yang stabil, presisi, dan diakui oleh sistem sebagai sinyal hardware asli.
              </p>
            </div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 font-mono text-sm relative overflow-hidden group shadow-2xl">
            <div className="absolute top-0 right-0 p-4 opacity-10">
                <Cpu className="h-24 w-24 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4 border-b border-slate-800 pb-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-slate-500 ml-2">signal_processor.log</span>
              </div>
              <pre className="text-blue-300 overflow-x-auto">
{`{
  "module": "GNSS_Driver_Bridge",
  "status": "CALIBRATING",
  "operations": [
    {
      "task": "Noise_Cancellation",
      "target": "Atmospheric_Interference",
      "result": "FILTERED"
    },
    {
      "task": "Coordinate_Lock",
      "input_source": "MANUAL_REFERENCE",
      "confidence": "100%"
    }
  ]
}`}
              </pre>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="bg-white border border-slate-200 rounded-3xl p-8 mb-24 overflow-hidden shadow-xl">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Perbedaan Metode</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="py-4 px-6 text-slate-500 font-medium uppercase tracking-wider text-sm">Metode</th>
                  <th className="py-4 px-6 text-red-500 font-bold w-1/3">Fake GPS Biasa</th>
                  <th className="py-4 px-6 text-brand-primary font-bold w-1/3">XCape Calibration</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-4 px-6 font-medium text-slate-900">Sumber Data</td>
                  <td className="py-4 px-6 text-sm">Mock Location API (Software)</td>
                  <td className="py-4 px-6 text-sm font-semibold text-brand-primary">Virtual Hardware Driver</td>
                </tr>
                <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-4 px-6 font-medium text-slate-900">Kestabilan Sinyal</td>
                  <td className="py-4 px-6 text-sm">Statis Total (Sering Error)</td>
                  <td className="py-4 px-6 text-sm font-semibold text-brand-primary">Simulasi Mikro-Gerakan (Natural)</td>
                </tr>
                <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-4 px-6 font-medium text-slate-900">Respon Aplikasi</td>
                  <td className="py-4 px-6 text-sm">Sering Terdeteksi</td>
                  <td className="py-4 px-6 text-sm font-semibold text-brand-primary">Diterima sebagai "Sinyal Kuat"</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Detailed Detection Vectors */}
        <div className="mb-24">
          <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">Parameter yang Dioptimalkan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Vector 1 */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-brand-primary transition-all shadow-sm hover:shadow-lg">
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Wifi className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Network Triangulation</h3>
              <p className="text-sm text-slate-600">
                Menyelaraskan data lokasi dengan profil jaringan Wi-Fi dan menara seluler di sekitar titik referensi, memastikan konsistensi data.
              </p>
            </div>

            {/* Vector 2 */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-brand-primary transition-all shadow-sm hover:shadow-lg">
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Activity className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Sensor Fusion</h3>
              <p className="text-sm text-slate-600">
                Menyuntikkan data akselerometer dan giroskop yang sesuai dengan pola pergerakan manusia, menghindari deteksi anomali "perangkat diam".
              </p>
            </div>

            {/* Vector 3 */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-brand-primary transition-all shadow-sm hover:shadow-lg">
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">System Integrity</h3>
              <p className="text-sm text-slate-600">
                Karena tidak memodifikasi file sistem (non-root), status integritas perangkat tetap terjaga, aman untuk aplikasi sensitif.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-16 text-center">
             <Link to="/harga" className="inline-flex items-center px-8 py-4 bg-brand-primary hover:bg-orange-600 text-white rounded-full font-bold transition-all shadow-lg shadow-orange-500/30">
                <Zap className="mr-2 h-5 w-5" />
                Mulai Kalibrasi Sekarang
             </Link>
        </div>

      </div>
    </div>
  );
};

export default Technology;